import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Inbox, Loader2, MessageSquareText, Phone, RefreshCw, Tag } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMessages() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contacts`, { withCredentials: true });
      setContacts(res.data);
    } catch {
      toast.error("Não foi possível carregar as mensagens.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  return (
    <section data-testid="admin-messages-panel">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-moss">Pedidos e dúvidas enviados pelo formulário do site.</p>
        <div className="flex items-center gap-3">
          <span
            data-testid="admin-message-count"
            className="rounded-full bg-olive px-4 py-2 font-mono text-xs font-semibold uppercase tracking-widest text-parchment"
          >
            {contacts.length} mensagens
          </span>
          <button
            type="button"
            onClick={loadContacts}
            data-testid="admin-refresh-button"
            className="inline-flex items-center gap-2 rounded-full border border-linen bg-white px-4 py-2 text-sm font-medium text-olive transition-colors hover:bg-linen/50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Atualizar
          </button>
        </div>
      </div>

      {loading && contacts.length === 0 ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-olive" />
        </div>
      ) : contacts.length === 0 ? (
        <div
          data-testid="admin-empty-state"
          className="flex flex-col items-center gap-4 rounded-[2rem] border border-dashed border-linen bg-white/60 py-20 text-center"
        >
          <Inbox className="h-10 w-10 text-moss/50" />
          <p className="font-display text-xl text-ink">Nenhuma mensagem por aqui ainda</p>
          <p className="max-w-sm text-sm text-moss">
            Quando um cliente enviar o formulário do site, a mensagem aparece aqui na hora.
          </p>
        </div>
      ) : (
        <ul className="space-y-4">
          {contacts.map((c) => (
            <li
              key={c.id}
              data-testid={`admin-message-${c.id}`}
              className="rounded-[1.5rem] border border-linen bg-white p-6 shadow-sm"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-forest font-display text-sm font-semibold text-ochre">
                    {c.name.charAt(0).toUpperCase()}
                  </span>
                  <div>
                    <p className="font-semibold text-ink">{c.name}</p>
                    <p className="text-xs text-moss">
                      {new Date(c.created_at).toLocaleString("pt-BR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-linen/60 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-olive">
                  <Tag className="h-3 w-3" />
                  {c.interest}
                </span>
              </div>
              <p className="mt-4 flex items-start gap-2 text-sm leading-relaxed text-ink/80">
                <MessageSquareText className="mt-0.5 h-4 w-4 shrink-0 text-clay" />
                {c.message}
              </p>
              <p className="mt-3 flex items-center gap-2 text-sm text-moss">
                <Phone className="h-4 w-4 text-clay" />
                {c.phone}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
